<template>
  <div class="tw-flex tw-flex-col tw-gap-1" data-testid="sf-radio">
    <label
      class="sf-radio tw-m-0 tw-flex tw-items-center tw-gap-1.5 tw-font-normal"
      :class="disabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'"
    >
      <input
        v-bind="$attrs"
        type="radio"
        class="sf-radio__input tw-sr-only"
        :name="name"
        :value="String(value)"
        :checked="checked"
        :disabled="disabled"
        :aria-describedby="description ? descriptionId : undefined"
        @change="select"
      />

      <span
        aria-hidden="true"
        class="
          sf-radio__control
          tw-flex
          tw-h-[18px]
          tw-w-[18px]
          tw-shrink-0
          tw-items-center
          tw-justify-center
          tw-rounded-full
          tw-border
          tw-border-solid
          tw-bg-white
          tw-shadow-sm
        "
        data-testid="sf-radio-control"
      >
        <span class="sf-radio__dot tw-h-3 tw-w-3 tw-rounded-full" />
        <span class="sf-radio__preview tw-h-2 tw-w-2 tw-rounded-full" />
      </span>

      <span class="sf-radio__text tw-text-sm tw-text-sf-slate-dark-1">{{
        label
      }}</span>

      <span
        v-if="note"
        class="
          tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-sf-slate-light-2
        "
        data-testid="sf-radio-note"
      >
        <i class="fa fa-circle-info" aria-hidden="true" />
        {{ note }}
      </span>
    </label>

    <p
      v-if="description"
      :id="descriptionId"
      class="tw-m-0 tw-pl-6 tw-text-sm tw-text-sf-slate-dark-1"
      data-testid="sf-radio-description"
    >
      {{ description }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, useId } from 'vue'

import type { SfRadioValue } from '@app/shared/vue/components/sf-radio/types'

defineOptions({
  name: 'SfRadio',
  inheritAttrs: false,
})

interface Props {
  value: SfRadioValue
  label: string
  modelValue?: SfRadioValue
  name?: string
  description?: string
  note?: string
  disabled?: boolean
}

const props = defineProps<Props>()

const emit =
  defineEmits<{
    'update:modelValue': [value: SfRadioValue]
  }>()

const descriptionId = `sf-radio-description-${useId()}`

const checked = computed(() => props.modelValue === props.value)

function select(): void {
  if (props.disabled) return

  emit('update:modelValue', props.value)
}
</script>

<style lang="scss" scoped>
.sf-radio__control {
  border-color: theme('colors.sf-slate.lighter-2');
}

.sf-radio__dot,
.sf-radio__preview {
  display: none;
}

// Brand colors run through the white-label variable, not the #06b6d4
// fallback, so themed firms get their own color.
.sf-radio__input:checked ~ .sf-radio__control {
  border-color: var(--color-sky-dark, #0891b2);

  .sf-radio__dot {
    display: block;
    background-color: var(--color-sky-default, #06b6d4);
  }
}

.sf-radio__input:focus-visible ~ .sf-radio__control {
  box-shadow: 0 0 0 4px
      color-mix(in srgb, var(--color-sky-default, #06b6d4) 25%, transparent),
    0 1px 2px 0 rgb(0 0 0 / 5%);
}

// Disabled dims the control and the label through one opacity each, so both
// keep the color they would have had.
.sf-radio__input:disabled ~ .sf-radio__control,
.sf-radio__input:disabled ~ .sf-radio__text {
  opacity: 0.4;
}

// Hover previews the selection with a smaller, paler dot. The design shows
// the same preview whether or not the option is already selected, so it
// replaces the solid dot while the pointer is over the row.
.sf-radio:hover .sf-radio__input:not(:disabled) ~ .sf-radio__control {
  border-color: var(--color-sky-dark, #0891b2);

  .sf-radio__dot {
    display: none;
  }

  .sf-radio__preview {
    display: block;
    background-color: var(--color-sky-default, #06b6d4);
    opacity: 0.4;
  }
}
</style>
